import {
    useCallback,
    useEffect,
    useState,
  } from "react";
  
  import {
    getSocket,
  } from "../lib/socket";
  
  import {
    patchProfileSettingsCache,
    prefetchProfileSettingsData,
    readProfileSettingsCache,
    resolveCurrentUserId,
    subscribeProfileSettingsCache,
  } from "../utils/profileSettingsCache";
  
  let socketConsumerCount =
    0;
  
  let sharedSocket =
    null;
  
  let socketRefreshTimer =
    null;
  
  const scheduleRefresh =
    () => {
      if (
        socketRefreshTimer
      ) {
        window.clearTimeout(
          socketRefreshTimer
        );
      }
  
      socketRefreshTimer =
        window.setTimeout(
          () => {
            socketRefreshTimer =
              null;
  
            prefetchProfileSettingsData({
              force: true,
            }).catch(
              (
                error
              ) => {
                console.warn(
                  "[useProfileSettingsData] websocket refresh failed:",
                  error
                );
              }
            );
          },
          80
        );
    };
  
  const isCurrentUserEvent =
    (
      payload
    ) => {
      const currentUserId =
        resolveCurrentUserId();
  
      if (!currentUserId) {
        /*
         * Defensive fallback.
         * If the token does not expose an ID, refresh rather than miss
         * a potentially relevant update.
         */
        return true;
      }
  
      const eventUserId =
        payload?.user?.id ??
        payload?.user?.user_id ??
        payload?.full?.id ??
        payload?.full?.user_id ??
        payload?.id ??
        null;
  
      if (
        eventUserId ===
          null ||
        eventUserId ===
          undefined
      ) {
        return false;
      }
  
      return (
        String(
          eventUserId
        ) ===
        String(
          currentUserId
        )
      );
    };
  
  const startSocket =
    () => {
      if (
        sharedSocket
      ) {
        return;
      }
  
      sharedSocket =
        getSocket();
  
      const subscribe =
        () => {
          sharedSocket?.emit(
            "subscribe:users"
          );
        };
  
      const onUpdated =
        (
          payload
        ) => {
          if (
            isCurrentUserEvent(
              payload
            )
          ) {
            scheduleRefresh();
          }
        };
  
      sharedSocket.__profileSettingsSubscribe =
        subscribe;
  
      sharedSocket.__profileSettingsUpdated =
        onUpdated;
  
      subscribe();
  
      sharedSocket.on(
        "connect",
        subscribe
      );
  
      sharedSocket.on(
        "reconnect",
        subscribe
      );
  
      sharedSocket.on(
        "user:updated",
        onUpdated
      );
    };
  
  const stopSocket =
    () => {
      if (
        !sharedSocket
      ) {
        return;
      }
  
      if (
        socketRefreshTimer
      ) {
        window.clearTimeout(
          socketRefreshTimer
        );
  
        socketRefreshTimer =
          null;
      }
  
      const subscribe =
        sharedSocket
          .__profileSettingsSubscribe;
  
      const onUpdated =
        sharedSocket
          .__profileSettingsUpdated;
  
      sharedSocket.emit(
        "unsubscribe:users"
      );
  
      if (subscribe) {
        sharedSocket.off(
          "connect",
          subscribe
        );
  
        sharedSocket.off(
          "reconnect",
          subscribe
        );
      }
  
      if (onUpdated) {
        sharedSocket.off(
          "user:updated",
          onUpdated
        );
      }
  
      delete sharedSocket
        .__profileSettingsSubscribe;
  
      delete sharedSocket
        .__profileSettingsUpdated;
  
      sharedSocket =
        null;
    };
  
  const registerSocketConsumer =
    () => {
      socketConsumerCount +=
        1;
  
      startSocket();
  
      return () => {
        socketConsumerCount =
          Math.max(
            0,
            socketConsumerCount -
              1
          );
  
        if (
          socketConsumerCount ===
          0
        ) {
          stopSocket();
        }
      };
    };
  
  export function useProfileSettingsData() {
    const [
      data,
      setData,
    ] =
      useState(
        () =>
          readProfileSettingsCache()
      );
  
    useEffect(() => {
      let alive = true;
  
      const unsubscribe =
        subscribeProfileSettingsCache(
          (
            nextData
          ) => {
            if (!alive) {
              return;
            }
  
            setData(
              nextData ||
                null
            );
          }
        );
  
      prefetchProfileSettingsData()
        .catch(
          (
            error
          ) => {
            console.warn(
              "[useProfileSettingsData] background refresh failed:",
              error
            );
          }
        );
  
      return () => {
        alive = false;
  
        unsubscribe();
      };
    }, []);
  
    useEffect(
      () =>
        registerSocketConsumer(),
      []
    );
  
    const refresh =
      useCallback(
        () =>
          prefetchProfileSettingsData({
            force: true,
          }),
        []
      );
  
    const patch =
      useCallback(
        (
          updater
        ) =>
          patchProfileSettingsCache(
            updater
          ),
        []
      );
  
    return {
      data,
      refresh,
      patch,
  
      hasCachedData:
        Boolean(
          data
        ),
    };
  }